<template>
    <div class="m-3" style="height: 90vh">
      <ReportsTable ref="rtable"
                    :settings="{
                      title: 'Orders Statement Report',
                      hideTable: true, 
                      hideCards: false,
                      hideExport: true, 
                      customRowsKey: null,
                      tableType: null,
                      theme: null,
                      dateRangeFormat: null,
                      filterRequired: false,
                      locationRequired: true, 
                      periodRequired: false,
                      appendQueryParams: {},
                      selectedPeriod: 'today'
                    }"
                    :columns="columnDefs"
                    @onDataLoaded="onDataLoaded"
                    endpoint="reports/sales/orders-statement">
        <template #filter>
          <el-select
              filterable
              clearable
              value-key="name"
              :placeholder="t('selectCustomer')"
              v-model="selectedCustomer"
              @change="onSelectedCustomerChanged"
              remote
              :remote-method="searchCustomer"
          >
            <el-option
                v-for="option in customers"
                :key="option.id"
                :label="option.name"
                :value="option"
            ></el-option>
          </el-select>
          <!-- <el-select
              filterable
              clearable
              class="col-span-12 md:col-span-3"
              value-key="fullName"
              :placeholder="t('selectEmployee')"
              v-model="selectedEmployee"
              @change="onSelectedEmployeeChanged"
              remote
              :remote-method="searchEmployees"
          >
            <el-option
                v-for="option in employees"
                :key="option.id"
                :label="option.fullName"
                :value="option"
            ></el-option>
          </el-select> -->
          <el-input v-model="companyNameLabel" :placeholder="t('companyName')" />
          <el-input v-model="numberLabel" :placeholder="t('number')" />
        </template>
        <template #cards="slotProps">
          <div class="bg-white p-6 shadow-lg mx-auto w-full mt-4">
            <!-- Header -->
            <div class="header mb-6 border-b-2 border-gray-300 pb-4 text-xl">
              <div class="flex flex-col">
                <div class="flex gap-2">
                  <span class="font-bold">{{ companyNameLabel }}</span>
                </div>
                <div class="flex gap-2">
                  <span class="font-bold">{{ t('date') }}</span>
                  <span v-if="slotProps?.data?.from_date != slotProps?.data?.to_date">{{ slotProps?.data?.from_date }} - {{ slotProps?.data?.to_date }}</span>
                  <span v-else>{{ slotProps?.data?.from_date }}</span>
                </div>
                <div class="flex gap-2">
                  <span class="font-bold">{{ t('number') }}</span>
                  <span>{{ numberLabel }}</span>
                </div>
              </div>
            </div>

            <!-- Print Button -->
            <div class="mb-4 text-left">
              <el-button type="primary" @click="printTable" class="no-print">
                <i class="icon-print mr-2"></i>
                {{ t('print') }}
              </el-button>
            </div>

            <!-- Orders Table -->
            <div v-if="slotProps?.data?.orders && slotProps?.data?.orders?.length > 0">
              <table class="w-full text-sm">
                <thead style="background-color: #DEE9FC;">
                  <tr>
                    <th class="px-3 py-2 font-bold text-start">{{ t('customer') }}</th>
                    <th class="px-3 py-2 font-bold text-start">{{ t('orderNumber') }}</th>
                    <th class="px-3 py-2 font-bold text-start">{{ t('item') }}</th>
                    <th class="px-3 py-2 font-bold text-start">{{ t('price') }}</th>
                    <th class="px-3 py-2 font-bold text-start">{{ t('quantity') }}</th>
                    <th class="px-3 py-2 font-bold text-start">{{ t('total') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <template v-for="(order, orderIndex) in slotProps.data.orders" :key="order.id">
                    <!-- Order Items -->
                    <template v-for="(item, itemIndex) in order.orderItems" :key="`${order.id}-${itemIndex}`">
                      <tr :class="[
                        'hover:bg-blue-50',
                        orderIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                      ]">
                        <td class="px-3 py-2">
                          {{ order.invoice?.customer?.name || t('cashCustomer') }}
                        </td>
                        <td class="px-3 py-2 font-mono">
                          {{ order.externalID }}
                        </td>
                        <td class="px-3 py-2">
                          {{ item.item?.name || item.item?.arName || t('notDefined') }}
                        </td>
                        <td class="px-3 py-2">
                          {{ formatMoney(item.soldAtPriceAmount || item.itemSalePriceAmount, 'IQD') }}
                        </td>
                        <td class="px-3 py-2">
                          {{ item.quantity }}
                        </td>
                        <td class="px-3 py-2">
                          {{ formatMoney(calculateItemTotal(item), 'IQD') }}
                        </td>
                      </tr>
                    </template>
                    <!-- Order Total Row -->
                    <tr :class="[
                      'font-bold',
                      orderIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                    ]">
                      <td colspan="5" class="px-3 py-2 text-right">
                      </td>
                      <td class="px-3 py-2 font-bold text-blue-600">
                        {{ formatMoney(calculateOrderTotal(order), 'IQD') }}
                      </td>
                    </tr>
                  </template>
                </tbody>
              </table>
              
              <!-- Grand Total - Outside table to prevent repetition -->
              <div class="bg-gray-100 font-semibold p-3 mt-2 flex justify-between border-t-2 border-gray-300 order-summary-footer">
                <span class="font-bold">{{ t('grandTotal') }}</span>
                <span class="font-bold text-green-600">
                  {{ formatMoney(calculateGrandTotal(slotProps.data.orders), 'IQD') }}
                </span>
              </div>
            </div>

            <!-- No Data Message -->
            <div v-else class="text-center py-8 text-gray-500">
              <i class="icon-info-circle text-4xl mb-2 block"></i>
              <p>{{ t('noData') }}</p>
            </div>
          </div>
        </template>
      </ReportsTable>
    </div>
  </template>
  
  <script setup lang="ts">
  import {onBeforeMount, provide, ref} from 'vue'
  import {useI18n} from 'vue-i18n'
  import {ColDef} from 'ag-grid-community'
  import {useRoute, useRouter} from 'vue-router'
  import {useHTTP} from '@/plugins/http'
  import {useAlertModal} from '@/plugins/alert-modal/alert-modal'
  import {formatMoney} from '@/utils/money'
  import ReportsTable from '@/views/reports/v2/table/ReportsTable.vue'
  import User from '@/models/user/User'
  import Customer from "@/models/customer/Customer";
  import {i18nOrderMessages} from "@/models/sales/Order";
import { formatDate } from '@/utils/date'
import { useSettingsStore } from '@/stores/settingsStore'
import { useContext } from '@/plugins/context'
  
  
  // i18n
  const {t} = useI18n({
    messages: {
      en: {
        ...i18nOrderMessages.en,
        selectEmployee: "Select Cashier",
        selectCustomer: "Select Customer",
        print: "Print Report",
        noData: "No data to display",
        customer: "Customer",
        orderNumber: "Order Number",
        item: "Item",
        price: "Price",
        quantity: "Quantity",
        total: "Total",
        cashCustomer: "Cash Customer",
        notDefined: "Not Defined",
        date: "Date",
        number: "Number",
        orderTotal: "Order Total",
        grandTotal: "Grand Total",
        companyName: "Company Name"
      },
      ar: {
        ...i18nOrderMessages.ar,
        selectEmployee: "اختر الموظف",
        selectCustomer: "اختر الزبون",
        print: "طباعة التقرير",
        noData: "لا توجد بيانات لعرضها",
        customer: "الزبون",
        orderNumber: "رقم الطلب",
        item: "المادة",
        price: "السعر",
        quantity: "الكمية",
        total: "الإجمالي",
        cashCustomer: "زبون نقدي",
        notDefined: "غير محدد",
        date: "التاريخ",
        number: "العدد",
        orderTotal: "إجمالي الطلب",
        grandTotal: "الإجمالي الكلي",
        companyName: "اسم الشركة"
      }
    }
  })
  
  // Data
  const rtable = ref<any>()
  const columnDefs = ref<ColDef[]>([])
  
  const http = useHTTP()
  const route = useRoute()
  const router = useRouter()
  const alertModal = useAlertModal()
  
  const employees = ref<User[] | any>();
  const selectedEmployee = ref<any>()
  
  
  const customers = ref<Customer[] | any>();
  const selectedCustomer = ref<any>()

  const companyNameLabel = ref<string>(useSettingsStore().getValue('company_name') || '')
  const numberLabel = ref<string>('')
  
  onBeforeMount(async () => {
    columnDefs.value = []
  })
  

  function calculateGrandTotal(orders: any[]): number {
    if (!orders || !Array.isArray(orders)) return 0
    
    return orders.reduce((total, order) => {
      const orderTotal = order.invoice.totalDueAmount || 0
      return total + orderTotal
    }, 0)
  }

  function printTable() {
    const printContent = document.querySelector('.bg-white.p-6.shadow-lg') as HTMLElement
    if (!printContent) return

    const printWindow = window.open('', '_blank')
    if (!printWindow) return

    // Detect current language direction from settings
    const currentLocale = useContext().locale || 'en'
    const currentDir = currentLocale === 'ar' ? 'rtl' : 'ltr'
    const isRTL = currentDir === 'rtl'

    printWindow.document.write(`
      <html dir="${currentDir}">
        <head>
          <title>Orders Statement Report</title>
          <style>
            * { 
              margin: 0; 
              padding: 0; 
              box-sizing: border-box; 
            }
            .header {
              font-size: 1.2rem;
              font-weight: 700;
              color: #374151;
              line-height: 1.5;
            }
            body { 
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
              padding: 1.5rem;
              color: #374151;
              line-height: 1.5;
              direction: ${document.documentElement.dir || 'ltr'};
            }
            
            /* Arabic/RTL Support */
            [dir="rtl"], [dir="rtl"] * {
              direction: rtl;
              text-align: right;
            }
            [dir="rtl"] .text-left {
              text-align: right;
            }
            [dir="rtl"] .text-right {
              text-align: left;
            }
            [dir="rtl"] table {
              direction: rtl;
            }
            [dir="rtl"] th,
            [dir="rtl"] td {
              text-align: right;
            }
            [dir="rtl"] .text-start {
              text-align: right;
            }
            [dir="rtl"] .justify-between {
              flex-direction: row-reverse;
            }

            [dir="rtl"] .order-summary-footer {
              flex-direction: row-reverse !important;
            }
            
            /* Additional RTL fixes for the footer */
            [dir="rtl"] .bg-gray-100.font-semibold.p-3.mt-2.flex.justify-between.border-t-2.border-gray-300.order-summary-footer {
              flex-direction: row-reverse !important;
            }
            
            /* Main container */
            .bg-white { background-color: white !important; }
            .p-6 { padding: 1.5rem; }
            .shadow-lg { box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1); }
            .mx-auto { margin-left: auto; margin-right: auto; }
            .w-full { width: 100%; }
            .mt-4 { margin-top: 1rem; }
            
            /* Header section */
            .mb-6 { margin-bottom: 1.5rem; }
            .border-b-2 { border-bottom-width: 2px; }
            .border-gray-300 { border-color: #d1d5db; }
            .pb-4 { padding-bottom: 1rem; }
            .flex { display: flex; }
            .flex-col { flex-direction: column; }
            .gap-4 { gap: 1rem; }
            .gap-2 { gap: 0.5rem; }
            .font-bold { font-weight: 700; }
            
            /* Print button */
            .mb-4 { margin-bottom: 1rem; }
            .text-left { text-align: left; }
            .no-print { display: none !important; }
            
            /* Table styles */
            .w-full { width: 100%; }
            .text-sm { font-size: 0.875rem; }
            table { 
              width: 100%; 
              border-collapse: collapse; 
              margin: 0;
            }
            
            /* Table header */
            thead { background-color: #DEE9FC !important; }
            .px-3 { padding-left: 0.75rem; padding-right: 0.75rem; }
            .py-2 { padding-top: 0.5rem; padding-bottom: 0.5rem; }
            .text-start { text-align: left; }
            th {
              background-color: #DEE9FC !important;
              padding: 0.5rem 0.75rem;
              font-weight: 700;
              text-align: left;
              -webkit-print-color-adjust: exact;
              color-adjust: exact;
            }
            
            /* Table body */
            td {
              padding: 0.5rem 0.75rem;
              text-align: left;
            }
            
            /* Row backgrounds */
            .bg-white { background-color: white !important; }
            .bg-gray-50 { 
              background-color: #f9fafb !important;
              -webkit-print-color-adjust: exact;
              color-adjust: exact;
            }
            .bg-gray-100 { 
              background-color: #f3f4f6 !important;
              -webkit-print-color-adjust: exact;
              color-adjust: exact;
            }
            
            /* Text styles */
            .font-mono { font-family: ui-monospace, SFMono-Regular, "SF Mono", Monaco, Consolas, monospace; }
            .font-semibold { font-weight: 600; }
            .text-right { text-align: right; }
            .text-blue-600 { 
              color: #2563eb !important;
              -webkit-print-color-adjust: exact;
              color-adjust: exact;
            }
            .text-green-600 { 
              color: #16a34a !important;
              -webkit-print-color-adjust: exact;
              color-adjust: exact;
            }
            
            /* Grand Total Section */
            .justify-between { justify-content: space-between; }
            .mt-2 { margin-top: 0.5rem; }
            .border-t-2 { border-top-width: 2px; }
            .border-gray-300 { border-color: #d1d5db; }
            .p-3 { padding: 0.75rem; }
            
            @media print {
              body { 
                padding: 1rem;
                font-size: 12px;
              }
              .no-print { display: none !important; }
              * { 
                -webkit-print-color-adjust: exact; 
                color-adjust: exact; 
              }
              .shadow-lg { box-shadow: none; }
              
                                        /* RTL fixes for print */
              [dir="rtl"] .order-summary-footer {
                flex-direction: row-reverse !important;
              }
              
              [dir="rtl"] .bg-gray-100.font-semibold.p-3.mt-2.flex.justify-between.border-t-2.border-gray-300.order-summary-footer {
                flex-direction: row-reverse !important;
              }
              
              /* RTL footer using order property for print */
              [dir="rtl"] .order-summary-footer > span:first-child {
                order: 2 !important;
              }
              
              [dir="rtl"] .order-summary-footer > span:last-child {
                order: 1 !important;
              }
            
            /* RTL footer using order property */
            [dir="rtl"] .order-summary-footer > span:first-child {
              order: 2;
            }
            
            [dir="rtl"] .order-summary-footer > span:last-child {
              order: 1;
            }
              
              /* Page numbering */
              @page {
                margin-bottom: 2cm;
                @bottom-center {
                  content: "${isRTL ? 'صفحة' : 'Page'} " counter(page) " ${isRTL ? 'من' : 'of'} " counter(pages);
                  font-size: 10px;
                  color: #666;
                  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
                  direction: ${currentDir};
                }
              }
              
              /* Prevent Grand Total section from breaking */
              .bg-gray-100 {
                break-inside: avoid;
                page-break-inside: avoid;
              }
              
              tr {
                page-break-inside: avoid;
                page-break-after: auto;
              }
            }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `)

    printWindow.document.close()
    printWindow.focus()
    setTimeout(() => {
      printWindow.print()
      printWindow.close()
    }, 250)
  }
  
  async function onSelectedEmployeeChanged(event: any) {
    rtable.value.payload.filter = selectedEmployee.value ? selectedEmployee.value.id : null;
    rtable.value.navigate(rtable.value.payload)
  }
  
  async function searchEmployees(query: string) {
    if (!query) {
      query = ''
    }
    try {
      const url = `${User.ENDPOINT}?q=${query}`
      const employeesRes = await http.get<User[]>(url)
      employees.value = employeesRes.map((employee) => User.from(employee))
    } catch (error) {
      employees.value = []
    }
  }
  
  
  async function onSelectedCustomerChanged(event: any) {
    rtable.value.payload.customer_id = selectedCustomer.value ?
        selectedCustomer.value.id : null;
    rtable.value.navigate(rtable.value.payload)
  }
  
  async function searchCustomer(query: string) {
    if (!query) {
      query = ''
    }
    try {
      const url = `${Customer.ENDPOINT}?q=${query}`
      const customersRes = await http.get<Customer[]>(url)
      customers.value = customersRes.map((customer) => Customer.from(customer))
    } catch (error) {
      customers.value = []
    }
  }
  
  
  function onDataLoaded(data: any) {
  }

  function calculateItemTotal(item: any): number {
    const price = item.soldAtPriceAmount || item.itemSalePriceAmount || 0
    const quantity = item.quantity || 0
    const returnedQuantity = item.returnedQuantity || 0
    return price * (quantity - returnedQuantity)
  }

  function calculateOrderTotal(order: any): number {
    return order.invoice.totalDueAmount || 0
  }


  </script>

  <style scoped>
  @media print {
    .no-print {
      display: none !important;
    }
  }
  </style>
  